import classnames from 'classnames';
import * as React from 'react';
import MinusIcon from '../../icons/MinusIcon';
import PlusIcon from '../../icons/PlusIcon';
import { FacetFiltersData } from '../../models/search';
import FacetSection from './FacetSection';
import {
  FacetFilterProps,
  FacetFilterState,
  FacetFilterValues,
  FacetSectionProps,
} from './models';
import styles from './styles/facetFilter.scss';
import '../../styles/h1Parity.scss';
import { FacetLabelData } from '../ProductList/models';

type FacetFiltersValues = {
  id: string;
  name: string;
  results: number;
};

//Kebab Case
const formatSectionValue = (value: string): string =>
  value
    .trim() // Remove leading/trailing spaces
    .replace(/([a-z])([A-Z])/g, '$1-$2') // Convert camelCase to kebab-case
    .toLowerCase() // Convert everything to lowercase
    .replace(/\s+/g, '-') // Replace spaces with a single hyphen
    .replace(/^-/, ''); // Remove leading hyphen if exists

const updateObjectWithMap = (
  initialData: FacetFiltersData,
  facetLabelData: FacetLabelData[]
): FacetFiltersData => {
  // Recursive function to update results object
  const recursiveUpdate = (item: any) => {
    // Check if 'title' needs to be updated
    if (
      item.title &&
      Array.isArray(facetLabelData) &&
      facetLabelData.some(
        (m) =>
          m.type === 'title' && m.key.toLowerCase() === item.title.toLowerCase()
      )
    ) {
      // Find the matching key and update the title
      const match = facetLabelData.find(
        (element) =>
          element.type === 'title' &&
          element.key.toLowerCase() === item.title.toLowerCase()
      );
      if (match) {
        item.title = match.value;
      }
    }

    // If the item has a 'values' array, update the 'name' in each object inside it
    if (Array.isArray(item.values)) {
      item.values = item.values.map((value: FacetFiltersValues) => {
        if (
          value.name &&
          Array.isArray(facetLabelData) &&
          facetLabelData.some(
            (m) =>
              m.type === 'value' &&
              m.key.toLowerCase() === value.name.toLowerCase()
          )
        ) {
          // Find the matching key and update the name
          const match = facetLabelData.find(
            (element) =>
              element.type === 'value' &&
              element.key.toLowerCase() === value.name.toLowerCase()
          );
          if (match) {
            value.name = match.value;
          }
        }
        return value;
      });
    }

    // If the item has other properties, we recursively update them too
    Object.keys(item).forEach((key) => {
      if (typeof item[key] === 'object') {
        item[key] = recursiveUpdate(item[key]);
      }
    });

    return item;
  };

  // Ensure that 'facetLabelData' is an array
  if (Array.isArray(facetLabelData)) {
    // Iterate over the keys of the FacetFiltersData object and apply recursiveUpdate
    Object.keys(initialData).forEach((key) => {
      initialData[key] = recursiveUpdate(initialData[key]);
    });
  } else {
    console.error('Error: The provided facetLabelData is not an array');
  }

  return initialData;
};

const FacetFilter = ({
  data,
  className = '',
  styled,
  onChange,
  showClearAll,
  enableCollapse,
  listLimit = 10,
  selectedValues: initialSelectedValues = null,
  title,
  renderNoResults,
  isSingleSelect,
  initialCollapsedSections = false,
  initFacetLabelsData = null,
}: FacetFilterProps) => {
  const [selectedValues, setSelectedValues] = React.useState<FacetFilterState>(
    valuesToSet(initialSelectedValues)
  );
  const [collapsedSections, setCollapsedSections] = React.useState(
    getCollapsedSectionsFromData(data)
  );

  React.useEffect(() => {
    data =
      initFacetLabelsData && updateObjectWithMap(data, initFacetLabelsData);
    let initialData = null;
    if (initialCollapsedSections) {
      initialData = Object.keys(data).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
    }
    setCollapsedSections(
      getCollapsedSectionsFromData(data, initialData || collapsedSections)
    );
  }, [data]);

  /*
    When this component is used as a controlled component, we sync the
    new received values with the internal state.
  */
  React.useEffect(() => {
    setSelectedValues(valuesToSet(initialSelectedValues));
  }, [initialSelectedValues]);

  React.useEffect(() => {
    const clearedData = {};
    for (const key in selectedValues) {
      if (isSingleSelect && selectedValues[key].size > 1) {
        clearedData[key] = [];
      }
    }
    setSelectedValues({ ...selectedValues, ...clearedData });
  }, [isSingleSelect]);

  const onFilterChange = (section: string, value: string | number) => {
    let newSectionState = new Set(
      selectedValues ? selectedValues[section] : []
    );
    let isChecked = true;
    if (isSingleSelect) {
      newSectionState = new Set([value]);
    } else {
      if (newSectionState.has(value)) {
        newSectionState.delete(value);
        isChecked = false;
      } else {
        newSectionState.add(value);
      }
    }

    const newState = {
      ...selectedValues,
      [section]: newSectionState,
    };

    onChange?.({
      isChecked,
      newValue: value,
      section,
      selectedValues: setToArray(newState),
    });
    setSelectedValues(newState);
  };

  const onClearAll = () => {
    setSelectedValues(null);
    onChange?.(null);
  };

  const onToggleCollpase = (section) => {
    setCollapsedSections({
      ...collapsedSections,
      [section]: !collapsedSections[section],
    });
  };

  const hasData = Object.keys(data || {}).length;

  if (!hasData) {
    return renderNoResults ? renderNoResults() : null;
  }

  return (
    <section className={className}>
      <div
        className={classnames(
          styles.titleContainer,
          'Sui-FacetFilter--title-container'
        )}
      >
        {title ? (
          <span className={classnames(styles.title, 'Sui-FacetFilter--title')}>
            {title}
          </span>
        ) : null}

        {hasData && showClearAll ? (
          <button
            className={classnames(
              styles.clearAllBtn,
              'Sui-FacetFilter--clear-all-btn'
            )}
            onClick={onClearAll}
          >
            Clear all
          </button>
        ) : null}
      </div>
      <ul className={styles.list}>
        {Object.keys(data || {})
          .sort((sectonA, sectionB) =>
            data[sectonA].order > data[sectionB].order ? 1 : -1
          )
          .map((section) => {
            const isSectionCollapsed = collapsedSections[section];
            const facetSectionProps: Omit<FacetSectionProps, 'onChange'> = {
              className: classnames(styles.section),
              isSingleSelect,
              limit: listLimit,
              section,
              selectedValues: selectedValues
                ? Array.from(selectedValues[section] || [])
                : [],
              styled: styled,
              type: data[section].type,
              values: data[section].values,
            };

            return (
              <li
                key={section}
                className={classnames(
                  styles.sectionContainer,
                  {
                    [styles.styledSectionContainer]: styled,
                  },
                  enableCollapse && isSectionCollapsed
                    ? styles.collapsed
                    : styles.expanded,
                  formatSectionValue(section),
                  `Sui-facet-section--${formatSectionValue(section)}`
                )}
              >
                {/* eslint-disable-next-line
                  jsx-a11y/click-events-have-key-events,
                  jsx-a11y/no-noninteractive-element-interactions
                  -- A click on the title toggles too; keyboard users toggle with the
                  button inside, so the heading stays a heading (DTW-1840). */}
                <h2
                  className={classnames(
                    styles.sectionTitle,
                    {
                      [styles.collapsible]: enableCollapse,
                      [styles.styledTitle]: styled,
                    },
                    'Sui-FacetFilter--facet-title'
                  )}
                  onClick={() => enableCollapse && onToggleCollpase(section)}
                >
                  <span>
                    {data[section].title}
                    {selectedValues?.[section]?.size ? (
                      <span
                        className={classnames(
                          styles.selectedCount,
                          { [styles.styledSelectedCount]: styled },
                          'Sui-FacetFilter--selected-count'
                        )}
                      >
                        {selectedValues[section].size}
                      </span>
                    ) : null}
                  </span>

                  {enableCollapse ? (
                    <button
                      className={classnames(
                        styles.expandCollapseBtn,
                        'Sui-FacetFilter--expand-collapse-btn'
                      )}
                      type="button"
                      aria-label={data[section].title || section}
                      aria-expanded={!isSectionCollapsed}
                    >
                      {isSectionCollapsed ? (
                        <PlusIcon size={15} />
                      ) : (
                        <MinusIcon size={15} />
                      )}
                    </button>
                  ) : null}
                </h2>

                <FacetSection
                  {...facetSectionProps}
                  onChange={(value) => onFilterChange(section, value)}
                />
              </li>
            );
          })}
      </ul>
    </section>
  );
};

export default FacetFilter;

function valuesToSet(initialValues: FacetFilterValues) {
  if (!initialValues) {
    return null;
  }
  return Object.keys(initialValues).reduce(
    (acc, key) => ({
      ...acc,
      [key]: new Set(initialValues[key] || []),
    }),
    {}
  );
}

function setToArray(newState: FacetFilterState) {
  const values = Object.keys(newState).reduce(
    (acc, key) => ({
      ...acc,
      ...(newState[key].size ? { [key]: Array.from(newState[key]) } : {}),
    }),
    {}
  );

  return Object.keys(values).length ? values : null;
}

function getCollapsedSectionsFromData(
  data: FacetFiltersData,
  collapsedSections = null
) {
  return Object.keys(data || {}).reduce((acc, key) => {
    if (
      data[key].collapsed ||
      (data[key].collapsed === undefined && collapsedSections?.[key])
    ) {
      return { ...acc, [key]: true };
    }
    return acc;
  }, {});
}
